import React, { useEffect, useMemo } from 'react';
import { FormControl, FormHelperText, Grid, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { Formik } from '@/types';
import ListSerie from '@/components/Stateful/ListSerie.tsx';
import ListTurma from '@/components/Stateful/ListTurma.tsx';
import ListTurno from '@/components/Stateful/ListTurno.tsx';
import InputMask from "react-input-mask";

type ResponsibleFormProps = {
  formik: Formik | any
}

function formatAddress({
  street,
  number,
  complement,
  neighborhood,
  city,
  uf,
  zip,
}: {
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  city?: string;
  uf?: string;
  zip?: string;
}) {
  const parts: string[] = [];

  if (street) {
    if (number) parts.push(`${street}, ${number}`);
    else parts.push(street);
  }

  if (complement) parts.push(complement);
  if (neighborhood) parts.push(neighborhood);

  let cityUf = '';
  if (city) cityUf += city;
  if (uf) cityUf += city ? `-${uf}` : uf;
  if (cityUf) parts.push(cityUf);

  if (zip) parts.push(`CEP ${zip}`);

  return parts.join(', ');
}


const ResponsibleForm: React.FC<ResponsibleFormProps> = ({ formik }) => {
  const addr = useMemo(
    () =>
      formatAddress({
        street: formik.values.address_street,
        number: formik.values.address_number,
        complement: formik.values.address_complement,
        neighborhood: formik.values.address_neighborhood,
        city: formik.values.address_city,
        uf: formik.values.address_uf,
        zip: formik.values.address_zip,
      }),
    [
      formik.values.address_street,
      formik.values.address_number,
      formik.values.address_complement,
      formik.values.address_neighborhood,
      formik.values.address_city,
      formik.values.address_uf,
      formik.values.address_zip,
    ],
  );

  useEffect(() => {
    if (addr !== formik.values.address) {
      formik.setFieldValue('address', addr, false); // não dispara validação a cada digitação
    }
  }, [addr]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <Stack display={'flex'} gap={2}>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <Typography>Nome do responsável*</Typography>
          <TextField
            name="name"
            id="name"
            fullWidth
            placeholder="José de Alencar"
            required
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.name && !!formik.errors.name}
            helperText={
              formik.touched.name &&
              formik.errors.name &&
              formik.errors.name
            }
          />
        </Grid>
        <Grid item sm={6}>
          <Typography></Typography>
          <InputMask
            mask="999.999.999-99"
            value={formik.values.cpf}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            {(inputProps: any) => (
              <TextField
                {...inputProps}
                fullWidth
                id="cpf"
                name="cpf"
                label="CPF"
                error={formik.touched.cpf && Boolean(formik.errors.cpf)}
                helperText={formik.touched.cpf && formik.errors.cpf}
                margin="normal"
                style={{margin: '23px 0 0 0'}}
              />
            )}
          </InputMask>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <Typography>Nome do Aluno*</Typography>
          <TextField
            name="student.nome"
            id="student.nome"
            fullWidth
            placeholder="Patativa do Assaré"
            required
            value={formik.values.student?.nome}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.student?.nome && !!formik.errors.student?.nome}
            helperText={
              formik.touched.student?.nome &&
              formik.errors.student?.nome &&
              formik.errors.student?.nome
            }
          />
        </Grid>
        <Grid item sm={6}>
          <ListSerie name={'serie_id'} formik={formik} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <ListTurma name={'turma_id'} formik={formik} serieId={formik.values.serie_id} />
        </Grid>
        <Grid item sm={6}>
          <ListTurno
            name={'turno_id'} formik={formik}
            serieId={formik.values.serie_id}
            turmaId={formik.values.turma_id} />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <Typography>Escola atual*</Typography>
          <TextField
            name="school"
            id="school"
            placeholder="E.E.F.M Eduquanto"
            required
            fullWidth
            value={formik.values.school}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.school && !!formik.errors.school}
            helperText={
              formik.touched.school &&
              formik.errors.school &&
              formik.errors.school
            }
          />
        </Grid>
        <Grid item sm={6} spacing={2}>
          <FormControl sx={{ '.MuiSvgIcon-root': { color: 'rgba(68, 68, 68, 1)' } }} fullWidth
                       error={formik.touched.where_did_you_meet && formik.errors.where_did_you_meet}>
            <Typography>Onde conheceu a escola*</Typography>
            <Select
              fullWidth
              labelId="demo-simple-select-label"
              sx={{ border: '1px solid #000' }}
              name="where_did_you_meet"
              id="where_did_you_meet"
              placeholder="José Filho"
              required
              value={formik.values.where_did_you_meet}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.where_did_you_meet && !!formik.errors.where_did_you_meet}
            >
              <MenuItem value={'google'}>Google</MenuItem>
              <MenuItem value={'facebook'}>Facebook</MenuItem>
              <MenuItem value={'instagram'}>Instagram</MenuItem>
              <MenuItem value={'outros'}>Outros</MenuItem>
            </Select>
            {
              formik.touched.where_did_you_meet && formik.errors.where_did_you_meet &&
              <FormHelperText>{formik.errors.where_did_you_meet}</FormHelperText>
            }
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <Typography>Telefone*</Typography>
          <InputMask
            mask="(99) 99999-9999"
            value={formik.values.phone}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            {(inputProps: any) => (
              <TextField
                {...inputProps}
                placeholder="(11) 98745-6123"
                name="phone"
                id="phone"
                required
                fullWidth
                error={formik.touched.phone && !!formik.errors.phone}
                helperText={formik.touched.phone && formik.errors.phone}
              />
            )}
          </InputMask>
        </Grid>
        <Grid item sm={6}>
          <Typography>E-mail*</Typography>
          <TextField
            placeholder="josealencar@gmail.com"
            name="email"
            id="email"
            required
            fullWidth
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && !!formik.errors.email}
            helperText={
              formik.touched.email &&
              formik.errors.email &&
              formik.errors.email
            }
          />
        </Grid>
      </Grid>

      {/* BLOCO DE ENDEREÇO DIVIDIDO */}
      <Grid container spacing={2}>
        <Grid item sm={12}>
          <Typography>Endereço</Typography>
        </Grid>

        <Grid item sm={8}>
          <TextField
            placeholder="Rua / Avenida"
            fullWidth
            name="address_street"
            id="address_street"
            value={formik.values.address_street}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address_street && !!formik.errors.address_street}
            helperText={formik.touched.address_street && formik.errors.address_street}
          />
        </Grid>

        <Grid item sm={4}>
          <TextField
            placeholder="Número"
            fullWidth
            name="address_number"
            id="address_number"
            value={formik.values.address_number}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address_number && !!formik.errors.address_number}
            helperText={formik.touched.address_number && formik.errors.address_number}
          />
        </Grid>

        <Grid item sm={6}>
          <TextField
            placeholder="Complemento (opcional)"
            fullWidth
            name="address_complement"
            id="address_complement"
            value={formik.values.address_complement}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address_complement && !!formik.errors.address_complement}
            helperText={formik.touched.address_complement && formik.errors.address_complement}
          />
        </Grid>

        <Grid item sm={6}>
          <TextField
            placeholder="Bairro"
            fullWidth
            name="address_neighborhood"
            id="address_neighborhood"
            value={formik.values.address_neighborhood}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address_neighborhood && !!formik.errors.address_neighborhood}
            helperText={formik.touched.address_neighborhood && formik.errors.address_neighborhood}
          />
        </Grid>

        <Grid item sm={6}>
          <TextField
            placeholder="Cidade"
            fullWidth
            name="address_city"
            id="address_city"
            value={formik.values.address_city}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.address_city && !!formik.errors.address_city}
            helperText={formik.touched.address_city && formik.errors.address_city}
          />
        </Grid>

        <Grid item sm={3}>
          <TextField
            placeholder="UF"
            fullWidth
            inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
            name="address_uf"
            id="address_uf"
            value={formik.values.address_uf}
            onChange={(e) => formik.setFieldValue('address_uf', e.target.value.toUpperCase())}
            onBlur={formik.handleBlur}
            error={formik.touched.address_uf && !!formik.errors.address_uf}
            helperText={formik.touched.address_uf && formik.errors.address_uf}
          />
        </Grid>

        <Grid item sm={3}>
          <InputMask
            mask="99999-999"
            value={formik.values.address_zip}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            {(inputProps: any) => (
              <TextField
                {...inputProps}
                fullWidth
                name="address_zip"
                id="address_zip"
                placeholder="CEP"
                error={formik.touched.address_zip && Boolean(formik.errors.address_zip)}
                helperText={formik.touched.address_zip && formik.errors.address_zip}
              />
            )}
          </InputMask>
        </Grid>

        {/* Pré-visualização do endereço completo (desabilitado) */}
        <Grid item sm={12}>
          <TextField
            label="Endereço completo"
            fullWidth
            name="address"
            id="address"
            value={formik.values.address || ''}
            disabled
          />
        </Grid>
      </Grid>
    </Stack>
  );
};

export default ResponsibleForm;